import { useState, useEffect } from 'react';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Lock, UserPlus, Shield, User, Trash2, Power, Globe, Link2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const SOCIAL_PLATFORMS = [
  { id: 'facebook', label: 'Facebook', color: '#1877F2', placeholder: 'facebook.com/sayfaniz' },
  { id: 'instagram', label: 'Instagram', color: '#E4405F', placeholder: '@kullaniciadi' },
  { id: 'twitter', label: 'X (Twitter)', color: '#1DA1F2', placeholder: '@kullaniciadi' },
  { id: 'linkedin', label: 'LinkedIn', color: '#0A66C2', placeholder: 'linkedin.com/company/...' },
  { id: 'tiktok', label: 'TikTok', color: '#000000', placeholder: '@kullaniciadi' },
  { id: 'pinterest', label: 'Pinterest', color: '#E60023', placeholder: 'pinterest.com/...' },
  { id: 'telegram', label: 'Telegram', color: '#0088CC', placeholder: '@kanal_adi' },
  { id: 'tumblr', label: 'Tumblr', color: '#36465D', placeholder: 'tumblr.com/blog/...' },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'EDITOR' });
  const [socials, setSocials] = useState([]);
  const [showAddSocial, setShowAddSocial] = useState(false);
  const [socialForm, setSocialForm] = useState({ platform: 'instagram', username: '', profile_url: '' });

  const loadUsers = () => {
    if (user?.role === 'SUPER_ADMIN' || user?.role === 'TENANT_ADMIN') {
      api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {});
    }
  };
  const loadSocials = () => {
    api.get('/social/accounts').then(r => setSocials(r.data)).catch(() => {});
  };
  useEffect(() => { loadUsers(); loadSocials(); }, [user]);

  const handlePasswordChange = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error('Tüm alanları doldurun'); return; }
    if (pwForm.new_password.length < 6) { toast.error('Yeni şifre en az 6 karakter olmalı'); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Yeni şifreler eşleşmiyor'); return; }
    setSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Şifre başarıyla değiştirildi');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Şifre değiştirme başarısız'); }
    setSaving(false);
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) { toast.error('Tüm alanları doldurun'); return; }
    if (newUser.password.length < 6) { toast.error('Şifre en az 6 karakter olmalı'); return; }
    try {
      await api.post('/auth/register', newUser);
      toast.success('Kullanıcı oluşturuldu');
      setShowAddUser(false);
      setNewUser({ email: '', password: '', name: '', role: 'EDITOR' });
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kullanıcı oluşturma başarısız'); }
  };

  const handleToggleUser = async (userId, currentActive) => {
    try {
      await api.put(`/auth/users/${userId}/status`, { is_active: !currentActive });
      toast.success(!currentActive ? 'Kullanıcı aktif yapıldı' : 'Kullanıcı pasif yapıldı');
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/auth/users/${userId}`);
      toast.success('Kullanıcı silindi');
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Silme başarısız'); }
  };

  const handleAddSocial = async () => {
    if (!socialForm.username) { toast.error('Kullanıcı adı gerekli'); return; }
    try {
      await api.post('/social/accounts', socialForm);
      toast.success('Sosyal medya hesabı bağlandı');
      setShowAddSocial(false);
      setSocialForm({ platform: 'instagram', username: '', profile_url: '' });
      loadSocials();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bağlantı başarısız'); }
  };

  const handleRemoveSocial = async (accountId) => {
    try {
      await api.delete(`/social/accounts/${accountId}`);
      toast.success('Hesap kaldırıldı');
      loadSocials();
    } catch { toast.error('Kaldırma başarısız'); }
  };

  const getPlatformInfo = (id) => SOCIAL_PLATFORMS.find(p => p.id === id) || { label: id, color: '#737373' };
  const roleLabel = { SUPER_ADMIN: 'Süper Admin', TENANT_ADMIN: 'Bayi Yönetici', EDITOR: 'Editör' };
  const roleColor = {
    SUPER_ADMIN: 'bg-red-500/10 text-red-400 border-red-500/20',
    TENANT_ADMIN: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    EDITOR: 'bg-green-500/10 text-green-400 border-green-500/20',
  };

  return (
    <div data-testid="settings-page" className="animate-fade-in space-y-6">
      <div className="pb-6 border-b border-[#262626]">
        <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Ayarlar</h1>
        <p className="text-[#A3A3A3] font-ibm text-sm mt-1">Hesap, kullanıcılar ve sosyal medya yönetimi</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile */}
        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader><CardTitle className="font-outfit text-lg text-white flex items-center gap-2"><User size={18} className="text-[#FACC15]" /> Profil Bilgileri</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#FACC15] flex items-center justify-center text-black font-bold text-xl font-outfit">{user?.name?.[0]?.toUpperCase()}</div>
              <div>
                <p className="text-white font-ibm font-medium">{user?.name}</p>
                <p className="text-sm text-[#A3A3A3] font-ibm">{user?.email}</p>
                <Badge variant="outline" className={`mt-1 text-xs ${roleColor[user?.role] || ''}`}>{roleLabel[user?.role] || user?.role}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Password */}
        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader><CardTitle className="font-outfit text-lg text-white flex items-center gap-2"><Lock size={18} className="text-[#FACC15]" /> Şifre Değiştir</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Mevcut Şifre</Label>
              <Input type="password" value={pwForm.current_password} onChange={e => setPwForm({...pwForm, current_password: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Yeni Şifre</Label>
              <Input type="password" value={pwForm.new_password} onChange={e => setPwForm({...pwForm, new_password: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="new-password-input" />
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Yeni Şifre (Tekrar)</Label>
              <Input type="password" value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="confirm-password-input" />
            </div>
            <Button onClick={handlePasswordChange} disabled={saving} data-testid="change-password-btn" className="w-full bg-white text-black hover:bg-[#E5E5E5] active:scale-[0.98] h-9">{saving ? 'Kaydediliyor...' : 'Şifreyi Değiştir'}</Button>
          </CardContent>
        </Card>
      </div>

      {/* Social Media Accounts */}
      <Card className="bg-[#141414] border-[#262626]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-outfit text-lg text-white flex items-center gap-2"><Globe size={18} className="text-[#FACC15]" /> Sosyal Medya Hesapları</CardTitle>
            <Button onClick={() => setShowAddSocial(true)} data-testid="add-social-btn" className="bg-white text-black hover:bg-[#E5E5E5] active:scale-[0.98] h-9 text-sm"><Plus size={14} className="mr-2" /> Hesap Bağla</Button>
          </div>
        </CardHeader>
        <CardContent>
          {socials.length === 0 ? (
            <p className="text-[#737373] font-ibm text-sm py-4 text-center">Henüz bağlı sosyal medya hesabı yok</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {socials.map(acc => {
                const info = getPlatformInfo(acc.platform);
                return (
                  <div key={acc.id} className="p-3 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A] hover:border-[#404040] transition-colors" data-testid={`social-${acc.platform}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: info.color + '20' }}>
                          <Link2 size={12} style={{ color: info.color }} />
                        </div>
                        <span className="text-sm font-ibm text-white font-medium">{info.label}</span>
                      </div>
                      <button onClick={() => handleRemoveSocial(acc.id)} className="text-[#525252] hover:text-red-400 transition-colors"><X size={14} /></button>
                    </div>
                    <p className="text-xs text-[#A3A3A3] font-ibm truncate">{acc.username}</p>
                    {acc.profile_url && <a href={acc.profile_url.startsWith('http') ? acc.profile_url : `https://${acc.profile_url}`} target="_blank" rel="noreferrer" className="text-xs text-[#525252] hover:text-[#FACC15] font-jetbrains truncate block mt-1">{acc.profile_url}</a>}
                    <Badge variant="outline" className="mt-2 text-xs bg-green-500/10 text-green-400 border-green-500/20">Bağlı</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Management */}
      {(user?.role === 'SUPER_ADMIN' || user?.role === 'TENANT_ADMIN') && (
        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-outfit text-lg text-white flex items-center gap-2"><Shield size={18} className="text-[#FACC15]" /> Kullanıcı Yönetimi</CardTitle>
              <Button onClick={() => setShowAddUser(true)} data-testid="add-user-btn" className="bg-white text-black hover:bg-[#E5E5E5] active:scale-[0.98] h-9 text-sm"><UserPlus size={14} className="mr-2" /> Yeni Kullanıcı</Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-[#262626] hover:bg-transparent">
                  <TableHead className="text-[#A3A3A3] font-ibm">Ad</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm">E-posta</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm">Rol</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm">Durum</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} className="border-[#262626] hover:bg-white/5">
                    <TableCell className="text-white font-ibm">{u.name}</TableCell>
                    <TableCell className="text-[#A3A3A3] font-ibm">{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${roleColor[u.role] || ''}`}>{roleLabel[u.role] || u.role}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${u.is_active ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>{u.is_active ? 'Aktif' : 'Pasif'}</Badge></TableCell>
                    <TableCell className="text-right">
                      {u.id !== user?.id && u.role !== 'SUPER_ADMIN' && (
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleToggleUser(u.id, u.is_active)} className={`h-7 w-7 p-0 ${u.is_active ? 'text-yellow-400 hover:text-yellow-300' : 'text-green-400 hover:text-green-300'}`} title={u.is_active ? 'Pasif yap' : 'Aktif yap'} data-testid={`toggle-user-${u.id}`}><Power size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className="h-7 w-7 p-0 text-[#525252] hover:text-red-400" title="Sil" data-testid={`delete-user-${u.id}`}><Trash2 size={14} /></Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader><DialogTitle className="font-outfit text-white">Yeni Kullanıcı Oluştur</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-[#A3A3A3] font-ibm text-sm">Ad Soyad</Label><Input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} data-testid="new-user-name" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Ahmet Yılmaz" /></div>
            <div><Label className="text-[#A3A3A3] font-ibm text-sm">E-posta</Label><Input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} data-testid="new-user-email" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" /></div>
            <div><Label className="text-[#A3A3A3] font-ibm text-sm">Şifre</Label><Input type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} data-testid="new-user-password" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="En az 6 karakter" /></div>
            {user?.role === 'SUPER_ADMIN' && (
              <div><Label className="text-[#A3A3A3] font-ibm text-sm">Rol</Label>
                <Select value={newUser.role} onValueChange={v => setNewUser({...newUser, role: v})}><SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]"><SelectItem value="TENANT_ADMIN">Bayi Yönetici</SelectItem><SelectItem value="EDITOR">Editör</SelectItem></SelectContent></Select></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)} className="border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-white/5">İptal</Button>
            <Button onClick={handleAddUser} data-testid="create-user-btn" className="bg-white text-black hover:bg-[#E5E5E5]">Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Social Dialog */}
      <Dialog open={showAddSocial} onOpenChange={setShowAddSocial}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader><DialogTitle className="font-outfit text-white">Sosyal Medya Hesabı Bağla</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Platform</Label>
              <Select value={socialForm.platform} onValueChange={v => setSocialForm({...socialForm, platform: v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="social-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {SOCIAL_PLATFORMS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Kullanıcı Adı / Hesap</Label>
              <Input value={socialForm.username} onChange={e => setSocialForm({...socialForm, username: e.target.value})} data-testid="social-username-input"
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder={getPlatformInfo(socialForm.platform).placeholder} />
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Profil URL (opsiyonel)</Label>
              <Input value={socialForm.profile_url} onChange={e => setSocialForm({...socialForm, profile_url: e.target.value})} data-testid="social-url-input"
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="https://..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSocial(false)} className="border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-white/5">İptal</Button>
            <Button onClick={handleAddSocial} data-testid="save-social-btn" className="bg-white text-black hover:bg-[#E5E5E5]">Bağla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
